import { lazy } from 'react'
import { Navigate, Route, Routes, useLocation, useOutletContext, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import RefLayout from '@/layouts/RefLayout'
import MenuLayout from '@/layouts/MenuLayout'
import type { MenuLayoutContext } from '@/layouts/MenuLayout'
import LocationPage from '@/pages/LocationPage'
import { StartOrder } from '@/pages/StartOrder'
import { PageLoader } from '@/components/common/PageLoader'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { PATHS, ROUTE_PATHS } from '@/router/paths'
import { RequireAuth } from '@/router/RequireAuth'
import { AlreadyAuth } from '@/router/AlreadyAuth'
import { getLocationByVanityRef } from '@/shared/api/location'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { useAppSelector } from '@/store'
import { selectSelectedOrderType, selectIsDeferred } from '@/store/ticketSlice'

// Index route — delegates to StartOrder only when no order type is set yet.
// Once the user completes order settings, selectedOrderType is populated in Redux and
// StartOrder no longer mounts — preventing it from immediately redirecting back to /order-settings.
// Pass through isStoreClosureActive to prevent navigation when store is closed or has dynamic closures.
//
// hasSameDayClosure exception: when a same-day closure is active (ASAP blocked, future slots exist),
// the user must pass through Order Settings even if selectedOrderType was restored from localStorage.
// Once they pick a future time, isDeferred becomes true and the guard lets the menu render normally.
// isStoreClosureActive (full blocking closure) is handled by the StoreClosed overlay, not here.
function StartOrderIndex() {
	const ctx = useOutletContext<MenuLayoutContext | null>()
	const selectedOrderType = useAppSelector(selectSelectedOrderType)
	const isDeferred = useAppSelector(selectIsDeferred)

	if (!ctx?.location) return null
	// Skip StartOrder when an order type is already chosen, unless a same-day closure is actively
	// blocking ASAP and the user hasn't yet picked a future time (isDeferred = false).
	if (selectedOrderType && (!ctx.hasSameDayClosure || ctx.isStoreClosureActive || isDeferred))
		return null

	return (
		<StartOrder
			location={ctx.location}
			isStoreClosureActive={ctx.isStoreClosureActive ?? false}
		/>
	)
}

const AddItemPage = lazy(() => import('@/pages/menu/AddItemPage'))
const EditItemPage = lazy(() => import('@/pages/menu/EditItemPage'))
const MenuSoldOutWarningPage = lazy(() => import('@/pages/menu/MenuSoldOutWarningPage'))
const OrderSettingsPage = lazy(() => import('@/pages/menu/OrderSettingsPage'))
const AccountEntryPage = lazy(() => import('@/pages/menu/account/AccountEntryPage'))
const UpdatePasswordPage = lazy(() => import('@/pages/menu/account/UpdatePasswordPage'))
const CheckoutPage = lazy(() => import('@/pages/checkout/CheckoutPage'))
const CheckoutSoldOutWarningPage = lazy(() => import('@/pages/checkout/CheckoutSoldOutWarningPage'))
const CheckoutAccountEntryPage = lazy(
	() => import('@/pages/checkout/account/CheckoutAccountEntryPage'),
)
const ConfirmationPage = lazy(() => import('@/pages/ConfirmationPage'))
const UserProfilePage = lazy(() => import('@/pages/userProfile/UserProfilePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

function VanityRedirect() {
	const { ref } = useParams<{ ref: string }>()
	const { search } = useLocation()

	const {
		data: location,
		isError,
		isPending,
	} = useQuery({
		queryKey: ['vanity', ref],
		queryFn: () => getLocationByVanityRef(ref!),
		staleTime: QUERY_STALE_TIMES.SHORT,
		retry: false,
	})

	if (isPending) return <PageLoader />
	if (isError || !location) return <Navigate to={PATHS.notFound()} replace />
	return <Navigate to={`${PATHS.menu(location.locationRef)}${search}`} replace />
}

export default function AppRoutes() {
	return (
		<Routes>
			<Route path={ROUTE_PATHS.VANITY} element={<VanityRedirect />} />
			{/* Explicit /not-found route must come before /:ref — React Router matches /:ref
			    for any single-segment path, so without this, /not-found would render LocationPage */}
			<Route path={ROUTE_PATHS.NOT_FOUND} element={<NotFoundPage />} />
			<Route path={ROUTE_PATHS.REF} element={<RefLayout />}>
				<Route
					element={
						<ErrorBoundary>
							<LocationPage />
						</ErrorBoundary>
					}
				>
					<Route element={<MenuLayout />}>
						<Route index element={<StartOrderIndex />} />
						<Route path={ROUTE_PATHS.ORDER_SETTINGS} element={<OrderSettingsPage />} />
						<Route
							path={ROUTE_PATHS.SOLD_OUT_WARNING}
							element={<MenuSoldOutWarningPage />}
						/>
						<Route path={ROUTE_PATHS.EDIT_ITEM} element={<EditItemPage />} />
						<Route path={ROUTE_PATHS.ADD_ITEM} element={<AddItemPage />} />
						<Route path={ROUTE_PATHS.ACCOUNT}>
							<Route index element={<Navigate to={ROUTE_PATHS.ENTRY} replace />} />
							<Route element={<AlreadyAuth />}>
								<Route path={ROUTE_PATHS.ENTRY} element={<AccountEntryPage />} />
							</Route>
							<Route
								path={ROUTE_PATHS.UPDATE_PASSWORD}
								element={<UpdatePasswordPage />}
							/>
						</Route>
					</Route>
					{/* CheckoutPage nested under LocationPage so useLocationBootstrap runs restoreCart
					    for the checkout tree too. Without this nesting, opening a checkout route
					    directly (e.g. /checkout/account/entry → /checkout after sign-in) never
					    hydrates the Redux cart from localStorage and the panel shows Your Order (0)
					    even though localStorage is correct (STO-3471). */}
					<Route
						path={ROUTE_PATHS.CHECKOUT}
						element={
							<ErrorBoundary>
								<CheckoutPage />
							</ErrorBoundary>
						}
					>
						<Route
							path={ROUTE_PATHS.SOLD_OUT_WARNING}
							element={<CheckoutSoldOutWarningPage />}
						/>
						<Route path={ROUTE_PATHS.ACCOUNT}>
							<Route index element={<Navigate to={ROUTE_PATHS.ENTRY} replace />} />
							<Route element={<AlreadyAuth redirectTo={PATHS.checkout} />}>
								<Route
									path={ROUTE_PATHS.ENTRY}
									element={<CheckoutAccountEntryPage />}
								/>
							</Route>
						</Route>
					</Route>
				</Route>
				<Route path={ROUTE_PATHS.CONFIRMATION} element={<ConfirmationPage />} />
				<Route element={<RequireAuth />}>
					<Route path={ROUTE_PATHS.USER_PROFILE} element={<UserProfilePage />} />
				</Route>
			</Route>
			<Route path={ROUTE_PATHS.NOT_FOUND_CATCH_ALL} element={<NotFoundPage />} />
		</Routes>
	)
}
